import React, { useRef, useState } from 'react';
import { toPng } from 'html-to-image';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Dialog,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import BrokenImageIcon from '@mui/icons-material/BrokenImage';

interface ImagePreviewProps {
  src: string;
  alt?: string;
  title?: string;
  index?: number;
}

const downloadImage = (src: string, title?: string) => {
  // Prefer direct download for URL images (faster, no canvas)
  const a = document.createElement('a');
  a.href = src;
  a.download = `${title || 'image'}.png`;
  a.target = '_blank';
  a.click();
};

// ─── Full-size popup ─────────────────────────────────────────────────────────

interface FullImageDialogProps {
  open: boolean;
  onClose: () => void;
  src: string;
  title?: string;
}

const FullImageDialog: React.FC<FullImageDialogProps> = ({ open, onClose, src, title }) => (
  <Dialog
    open={open}
    onClose={onClose}
    maxWidth="xl"
    fullWidth
    PaperProps={{
      sx: {
        borderRadius: 3,
        maxHeight: '90vh',
        m: 2,
      },
    }}
  >
    <DialogTitle
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        py: 1.5,
        px: 2,
        borderBottom: '1px solid #e5e7eb',
        fontSize: 14,
        fontWeight: 600,
        color: '#555',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <span>🖼️</span>
        <span>{title || 'Generated Image'}</span>
      </Box>
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        <Tooltip title="Download image" arrow>
          <IconButton
            size="small"
            onClick={() => downloadImage(src, title)}
            sx={{ color: '#667eea', '&:hover': { bgcolor: 'rgba(102,126,234,0.08)' } }}
          >
            <DownloadIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <IconButton size="small" onClick={onClose} sx={{ color: '#999' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
    </DialogTitle>

    <DialogContent
      sx={{
        p: 3,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        bgcolor: '#fafafa',
        overflowY: 'auto',
      }}
    >
      <img
        src={src}
        alt={title || 'Generated Image'}
        style={{
          maxWidth: '100%',
          maxHeight: '75vh',
          height: 'auto',
          borderRadius: 8,
          display: 'block',
          objectFit: 'contain',
        }}
      />
    </DialogContent>
  </Dialog>
);

// ─── Main component ───────────────────────────────────────────────────────────

const ImagePreview: React.FC<ImagePreviewProps> = ({ src, alt, title, index = 0 }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [imgError, setImgError] = useState(false);

  if (imgError) return null;

  return (
    <>
      {/* ── Compact thumbnail card ── */}
      <Box
        sx={{
          my: 1,
          bgcolor: '#f8f9fa',
          borderRadius: 2,
          border: '1px solid #e0e0e0',
          overflow: 'hidden',
          position: 'relative',
          cursor: 'pointer',
          display: 'inline-block',
          maxWidth: 300,
          width: '100%',
          transition: 'box-shadow 0.2s',
          '&:hover': {
            boxShadow: '0 2px 12px rgba(102,126,234,0.18)',
            borderColor: '#667eea',
          },
          '&:hover .img-overlay': {
            opacity: 1,
          },
        }}
        onClick={() => setDialogOpen(true)}
      >
        {/* Title bar */}
        

        {/* Thumbnail */}
        <Box sx={{ position: 'relative', height: 120, bgcolor: '#f0f0f0' }}>
          <img
            src={src}
            alt={alt || title || 'image'}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
            }}
            onError={() => setImgError(true)}
          />

          {/* Hover overlay */}
          <Box
            className="img-overlay"
            sx={{
              position: 'absolute',
              inset: 0,
              bgcolor: 'rgba(102,126,234,0.06)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: 0,
              transition: 'opacity 0.2s',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                bgcolor: 'rgba(255,255,255,0.92)',
                borderRadius: 2,
                px: 1.5,
                py: 0.75,
                border: '1px solid #667eea',
              }}
            >
              <ZoomInIcon sx={{ fontSize: 14, color: '#667eea' }} />
              <Typography sx={{ fontSize: 11, color: '#667eea', fontWeight: 600 }}>
                Click to expand
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* ── Full-size dialog ── */}
      <FullImageDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        src={src}
        title={title || alt}
      />
    </>
  );
};

export default ImagePreview;